// src/pages/portal/PortalDashboard.tsx
import { Link } from 'react-router-dom';
import { usePortalDashboard, usePortalConfig } from '@/hooks/queries';
import { Card, CardContent, CardHeader, CardTitle, Badge, Skeleton } from '@/components/ui';
import { Ticket, MessageSquare, CheckCircle, Clock, ArrowRight } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-8 w-48" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-32" />
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Skeleton className="h-48" />
        <Skeleton className="h-48" />
      </div>
    </div>
  );
}

export default function PortalDashboard() {
  const { data: dashboard, isLoading: loadingDashboard } = usePortalDashboard();
  const { data: config, isLoading: loadingConfig } = usePortalConfig();

  if (loadingDashboard || loadingConfig) {
    return <DashboardSkeleton />;
  }

  const estadoBadgeVariant = (estado: string) => {
    switch (estado?.toLowerCase()) {
      case 'abierto':
      case 'nuevo':
        return 'info';
      case 'en_progreso':
      case 'en progreso':
        return 'warning';
      case 'resuelto':
      case 'cerrado':
        return 'success';
      default:
        return 'default';
    }
  };

  return (
    <div className="space-y-6">
      {/* Mensaje de bienvenida */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Bienvenido al Portal de Soporte
        </h1>
        {config?.mensaje_bienvenida && (
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            {config.mensaje_bienvenida}
          </p>
        )}
      </div>

      {/* Tarjetas de resumen */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Tickets Abiertos
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {dashboard?.tickets_abiertos || 0}
                </p>
              </div>
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                <Ticket className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  En Progreso
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {dashboard?.tickets_en_progreso || 0}
                </p>
              </div>
              <div className="p-3 bg-yellow-100 dark:bg-yellow-900/30 rounded-full">
                <Clock className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Resueltos este Mes
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {dashboard?.tickets_resueltos_mes || 0}
                </p>
              </div>
              <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-full">
                <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Conversaciones
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {dashboard?.conversaciones_activas || 0}
                </p>
              </div>
              <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-full">
                <MessageSquare className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Acciones rapidas */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Link
          to="/portal/tickets/nuevo"
          className="p-4 bg-blue-600 hover:bg-blue-700 rounded-lg text-white flex items-center justify-between transition-colors"
        >
          <div className="flex items-center space-x-3">
            <Ticket className="w-6 h-6" />
            <span className="font-medium">Crear Nuevo Ticket</span>
          </div>
          <ArrowRight className="w-5 h-5" />
        </Link>

        <Link
          to="/portal/conversaciones/nueva"
          className="p-4 bg-purple-600 hover:bg-purple-700 rounded-lg text-white flex items-center justify-between transition-colors"
        >
          <div className="flex items-center space-x-3">
            <MessageSquare className="w-6 h-6" />
            <span className="font-medium">Iniciar Conversacion</span>
          </div>
          <ArrowRight className="w-5 h-5" />
        </Link>
      </div>

      {/* Ultimas actividades */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Último ticket */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center justify-between">
              <span>Último Ticket</span>
              <Link
                to="/portal/tickets"
                className="text-sm font-normal text-blue-600 hover:text-blue-700"
              >
                Ver todos
              </Link>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dashboard?.ultimo_ticket ? (
              <Link
                to={`/portal/tickets/${dashboard.ultimo_ticket.id}`}
                className="block p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 dark:text-white truncate">
                      {dashboard.ultimo_ticket.titulo}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      #{dashboard.ultimo_ticket.numero}
                    </p>
                  </div>
                  <Badge variant={estadoBadgeVariant(dashboard.ultimo_ticket.estado)}>
                    {dashboard.ultimo_ticket.estado}
                  </Badge>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  {formatDistanceToNow(new Date(dashboard.ultimo_ticket.fecha_creacion), {
                    addSuffix: true,
                    locale: es,
                  })}
                </p>
              </Link>
            ) : (
              <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                No tienes tickets registrados
              </p>
            )}
          </CardContent>
        </Card>

        {/* Última conversación */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center justify-between">
              <span>Última Conversación</span>
              <Link
                to="/portal/conversaciones"
                className="text-sm font-normal text-blue-600 hover:text-blue-700"
              >
                Ver todas
              </Link>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dashboard?.ultima_conversacion ? (
              <Link
                to={`/portal/conversaciones/${dashboard.ultima_conversacion.id}`}
                className="block p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <p className="font-medium text-gray-900 dark:text-white">
                  {dashboard.ultima_conversacion.asunto || 'Sin asunto'}
                </p>
                {dashboard.ultima_conversacion.ultimo_mensaje && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                    {dashboard.ultima_conversacion.ultimo_mensaje}
                  </p>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  {formatDistanceToNow(new Date(dashboard.ultima_conversacion.fecha), {
                    addSuffix: true,
                    locale: es,
                  })}
                </p>
              </Link>
            ) : (
              <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                No tienes conversaciones activas
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
